import { Settings, SquarePen } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useGetUserPreferences, useUpdateUserPreferences } from '~/api';
import {
  useGetMyProducts,
  useGetUniqueLocationsWithInventory,
  useUpsertSellerInventory,
  useUpsertSellerProductConfiguration,
} from '~/api/my-products';
import { Modal } from '~/components/overlays';
import {
  applyColumnOrder,
  type SortDirection,
  Table,
  type TableColumn,
  type TableRow,
  useColumnOrderPreference,
  useTable,
} from '~/components/ui/table';
import {
  SellerProductConfigurationScope,
  type SellerProductFiltersInput,
  type SellerProductInventoriesQuery,
  type SellerProductStatus,
} from '~/graphql/graphql';
import {
  type ClientError,
  buildScopeArgsFromConfiguration,
  extractGraphQLErrorMessages,
  parseNonNegativeInt,
  formatLengthWithBasis,
  formatLocationName,
} from '~/utils';
import { buildAndFilter } from '~/types';
import { ProductSettingModal } from '~/components/cards/ProductSettingModal';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { MyProductsToolbar } from './MyProductsToolbar';
import {
  filterRowsToClauses,
  searchTermClause,
} from '~/pages/my-products/lib/filterClauses';
import {
  categoryOf,
  withoutCategory,
} from '~/pages/my-products/lib/myProductsFilterRows';
import { useMyProductsSavedViews } from '~/pages/my-products/lib/useMyProductsSavedViews';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { useDebounce } from '~/hooks/useDebounce';
import { PageLoadingSpinner } from '~/components/ui/spinners/PageLoadingSpinner';
import { setSellerInventories } from '~/redux/reducers/inventory';
import { useDispatch } from 'react-redux';
import { LocationSelect } from '~/components/common/LocationSelect';
import { formatAddressText } from '~/utils/addressFormat';

type ReturnedSellerProductInventory = NonNullable<
  SellerProductInventoriesQuery['sellerProductInventories']['content'][0]
>;

// Sort is managed server-side; this callback is required by the Table API but is a no-op.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleTableSort = (_key: string, _direction: SortDirection) => {};

export function CurrentProducts() {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const inventoryViews = useMyProductsSavedViews('INVENTORY');
  const [searchClause, setSearchClause] = useState<SellerProductFiltersInput>();
  const [selectedLocationId, setSelectedLocationId] = useState('all');
  const [currentInventory, setCurrentInventory] =
    useState<ReturnedSellerProductInventory | null>(null);
  const isLocationInitialized = useRef(false);

  const debouncedSetSearchClause = useDebounce(
    (term: string) => setSearchClause(searchTermClause(term)),
    500
  );

  const filters = useMemo(() => {
    const clauses = filterRowsToClauses(
      withoutCategory(inventoryViews.rows),
      categoryOf(inventoryViews.rows)
    );
    if (selectedLocationId !== 'all') {
      clauses.push({ locationId: { eq: selectedLocationId } });
    }
    if (searchClause) clauses.push(searchClause);
    return buildAndFilter(clauses);
  }, [inventoryViews.rows, selectedLocationId, searchClause]);

  const navigate = useNavigate();
  const { t } = useTranslation('my-product');
  const { t: tGeneral } = useTranslation('general');
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { data: locationsData } = useGetUniqueLocationsWithInventory();
  const { data: preferencesData } = useGetUserPreferences();

  const { mutate: updatePreferences } = useUpdateUserPreferences();

  const {
    data,
    isLoading,
    isLoadingMore,
    sentinelRef,
    hasMoreData,
    updateItem,
  } = usePaginatedQuery<
    SellerProductInventoriesQuery,
    ReturnedSellerProductInventory,
    SellerProductFiltersInput
  >({
    fetchHook: useGetMyProducts,
    select: (data) => data?.sellerProductInventories,
    options: { filters },
  });

  //set the seller inventory in redux
  useEffect(() => {
    dispatch(setSellerInventories(data));
  }, [data, dispatch]);

  const {
    mutate: upsertSellerProductConfiguration,
    isPending: isUpsertConfigurationPending,
  } = useUpsertSellerProductConfiguration();

  const { mutate: upsertSellerInventory, isPending: isUpsertInventoryPending } =
    useUpsertSellerInventory();

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSetSearchClause(value);
  };

  useEffect(() => {
    if (!preferencesData || isLocationInitialized.current) return;
    isLocationInitialized.current = true;
    const savedLocationId =
      preferencesData.userPreferences?.sellerProductSelectedLocationId;
    if (!savedLocationId) return;
    setSelectedLocationId(savedLocationId);
  }, [preferencesData]);

  const handleLocationChange = (_name: string, value: string) => {
    setSelectedLocationId(value);
    updatePreferences({
      preferences: {
        sellerProductSelectedLocationId: value === 'all' ? '' : value,
      },
    });
  };

  const mutationHandler = {
    onSuccess: () => {
      toast.success(t('messages.product_updated_successfully'));
      setShowSettingsModal(false);
    },
    onError: (error: ClientError) =>
      toast.error(extractGraphQLErrorMessages(error).join(', ')),
  };

  // Inline table edits apply the new value to the local list directly rather than
  // relying on a post-mutation refetch. The mutation hook invalidates ['my-products']
  // on success, which kicks off a refetch; we cancel it first so a slow or
  // read-after-write-stale response can't land last and overwrite the value the
  // user just set (which previously left the cell stale until a hard refresh).
  // The invalidated query reconciles on the next natural fetch. Mirrors the buyer
  // InventoryTable edit path.
  const applyTableEdit = async (
    rowId: string,
    patch: (
      item: ReturnedSellerProductInventory
    ) => ReturnedSellerProductInventory
  ) => {
    await queryClient.cancelQueries({ queryKey: ['my-products'] });
    updateItem(rowId, patch);
    toast.success(t('messages.product_updated_successfully'));
    tableControls.handleEditDone();
  };

  const tableEditErrorHandler = (error: ClientError) =>
    toast.error(extractGraphQLErrorMessages(error).join(', '));

  const handleTableEditSubmit = (
    rowId: string,
    columnKey: string,
    newValue: string | number
  ) => {
    if (!data) return;
    const inventory = data.find((inv) => inv?.id === rowId);
    if (!inventory) return;

    if (
      columnKey === 'readyToShipDays' &&
      parseNonNegativeInt(newValue) === null
    ) {
      toast.error(t('edit_product.errors.ready_to_ship_negative'));
      tableControls.handleEditDone();
      return;
    }

    const platformProductLengthId = inventory.platformProductsLength.id;

    if (columnKey === 'stock') {
      const payload = {
        platformProductsLengthId: platformProductLengthId,
        locationId: inventory.location.id,
        quantity: {
          // Use the display unit (USER_PREFERRED) — the user typed in whatever
          // unit the table is currently showing, not the configured seller unit.
          value: Number(newValue),
          unit: inventory.quantity.unit,
        },
      };
      upsertSellerInventory(payload, {
        onSuccess: () =>
          void applyTableEdit(rowId, (item) => ({
            ...item,
            // Typed value in the display unit. The status badge is derived from
            // quantity at render time, so it updates from this alone.
            quantity: {
              value: Number(newValue),
              unit: inventory.quantity.unit,
            },
          })),
        onError: tableEditErrorHandler,
      });
      return;
    }

    // Editing any non-stock column upserts the SellerProductConfiguration. When the
    // product has no configuration yet, the backend creates one keyed to this
    // inventory and applies the edited field. We only reconstruct the replenishment
    // rate from an existing configuration — using the stored (PCS) value to avoid
    // round-trip drift (USER_PREFERRED → pieces → USER_PREFERRED can accumulate ±1
    // piece per save for rates with repeating-decimal ratios). For an unconfigured
    // product (when not editing the rate itself) it defaults to a zero rate in the
    // display unit, which is sent in the payload.
    const sellerProductConfiguration = inventory.sellerProductConfiguration;
    const storedReplenishment =
      sellerProductConfiguration?.replenishmentRateStored;
    const replenishmentRatePerWeek =
      columnKey === 'replenishmentRatePerWeek'
        ? {
            value: Number(newValue),
            unit:
              sellerProductConfiguration?.replenishmentRatePerWeek.unit ??
              inventory.quantity.unit,
          }
        : storedReplenishment
          ? {
              value: storedReplenishment.value,
              unit: storedReplenishment.unit,
            }
          : { value: 0, unit: inventory.quantity.unit };

    const payload = {
      platformProductLengthId: platformProductLengthId,
      // Inline edits never change scope — target the row's own configuration
      // scope so an org-wide config is not silently forked into a facility one.
      // When the product has no configuration yet, create a facility-scoped
      // config from the row's facility id.
      ...(sellerProductConfiguration
        ? buildScopeArgsFromConfiguration(sellerProductConfiguration)
        : {
            scope: SellerProductConfigurationScope.Facility,
            facilityId: inventory.location.facility.id,
          }),
      sellerProductConfigurationInput: {
        [columnKey]:
          columnKey !== 'replenishmentRatePerWeek'
            ? Number(newValue)
            : undefined,
        replenishmentRatePerWeek,
      },
    };
    upsertSellerProductConfiguration(payload, {
      onSuccess: () =>
        void applyTableEdit(rowId, (item) => {
          const config = item.sellerProductConfiguration;
          if (!config) return item;
          if (columnKey === 'replenishmentRatePerWeek') {
            return {
              ...item,
              sellerProductConfiguration: {
                ...config,
                replenishmentRatePerWeek: {
                  value: Number(newValue),
                  unit: replenishmentRatePerWeek.unit,
                },
              },
            };
          }
          if (columnKey === 'readyToShipDays') {
            return {
              ...item,
              sellerProductConfiguration: {
                ...config,
                readyToShipDays: Number(newValue),
              },
            };
          }
          return item;
        }),
      onError: tableEditErrorHandler,
    });
  };

  const tableControls = useTable({
    onSort: handleTableSort,
    onEditSubmit: handleTableEditSubmit,
  });

  const handleActionsClicked = (inventoryId: string, action: string) => {
    if (action === 'edit') {
      navigate(`/my-products/${inventoryId}/edit`);
      return;
    }
    if (!data) return;
    const currentInventory = data.find((inv) => inv?.id === inventoryId);
    if (currentInventory) {
      setCurrentInventory(currentInventory);
    }
    setShowSettingsModal(true);
  };

  const handleSaveSettings = (
    criticalStockLevel: number,
    lowStockLevel: number
  ) => {
    if (criticalStockLevel >= lowStockLevel) {
      toast.error(t('stock_monitoring.thresholds.critical_above_low_error'));
      return;
    }
    if (!currentInventory) return;

    // Saving stock-level settings upserts the configuration, creating it if the
    // product has none yet. Without an existing configuration there is no configured
    // unit, so fall back to the row's display unit and default the replenishment
    // rate to 0.
    const sellerProductConfiguration =
      currentInventory.sellerProductConfiguration;
    const currentUnit =
      sellerProductConfiguration?.unit ?? currentInventory.quantity.unit;
    // Stock-monitoring settings never change scope — target the row's own
    // configuration scope, never locationId.
    const payload = {
      platformProductLengthId: currentInventory.platformProductsLength.id,
      ...(sellerProductConfiguration
        ? buildScopeArgsFromConfiguration(sellerProductConfiguration)
        : {
            scope: SellerProductConfigurationScope.Facility,
            facilityId: currentInventory.location.facility.id,
          }),
      sellerProductConfigurationInput: {
        criticalStockLevel: { value: criticalStockLevel, unit: currentUnit },
        lowStockLevel: { value: lowStockLevel, unit: currentUnit },
        replenishmentRatePerWeek:
          sellerProductConfiguration?.replenishmentRateStored ?? {
            value: 0,
            unit: currentUnit,
          },
      },
    };
    upsertSellerProductConfiguration(payload, mutationHandler);
  };

  const { order, handleColumnReorderEnd } = useColumnOrderPreference(
    preferencesData?.userPreferences?.sellerInventoryColumnOrder,
    (newOrder, { onError, onSuccess }) =>
      updatePreferences(
        { preferences: { sellerInventoryColumnOrder: newOrder } },
        {
          onSuccess,
          onError: () => {
            onError();
            toast.error(tGeneral('table.reorder_save_error'));
          },
        }
      )
  );
  const columns: TableColumn[] = applyColumnOrder(getTableColumns(t), order);
  const rows: TableRow[] = getTableRows(data, handleActionsClicked, t);

  return (
    <>
      <Modal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      >
        <ProductSettingModal
          initialCriticalStockLevel={
            currentInventory?.sellerProductConfiguration?.criticalStockLevel
              ?.value || 0
          }
          initialLowStockLevel={
            currentInventory?.sellerProductConfiguration?.lowStockLevel
              ?.value || 0
          }
          stockQuantity={currentInventory?.quantity?.value || 0}
          replenishmentRate={
            currentInventory?.sellerProductConfiguration
              ?.replenishmentRatePerWeek?.value || 0
          }
          onClose={() => setShowSettingsModal(false)}
          onSave={handleSaveSettings}
          isSaving={isUpsertConfigurationPending || isUpsertInventoryPending}
        />
      </Modal>

      <div className="bg-neut-bg-alt rounded-lg border border-neut-border shadow-sm">
        <div className="px-6 py-4 border-b border-neut-border flex items-center justify-between">
          <div>
            <h2 className="text-lg text-neut-prim font-semibold">
              {t('current_products.title')}
            </h2>
            <p className="text-xs text-neut-sec mt-1">
              {t('current_products.subtitle')}
            </p>
          </div>
        </div>

        <div className="w-full grid grid-cols-1 sm:grid-cols-[minmax(0,18rem)_1fr_minmax(0,auto)] gap-4 items-end px-1 md:px-3 py-4">
          <LocationSelect
            label={t('current_products.filter.location')}
            name="locations"
            value={selectedLocationId}
            onChange={handleLocationChange}
            locations={locationsData?.uniqueLocationsWithInventory ?? []}
            allLabel={t('current_products.filter.all_locations')}
            getLabel={(location) =>
              `${location.name} - ${formatAddressText(location.facility.address)}`
            }
          />
          <MyProductsToolbar
            view="INVENTORY"
            views={inventoryViews}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
          />
        </div>

        <div className="overflow-x-auto ">
          <Table
            minWidth={55}
            rows={rows}
            columns={columns}
            tableControls={tableControls}
            enableColumnReorder
            onColumnReorderEnd={handleColumnReorderEnd}
          />
          {isLoading || isLoadingMore ? (
            <PageLoadingSpinner name="products" />
          ) : null}
        </div>

        <div ref={sentinelRef} className="w-full py-2"></div>

        {!isLoading && hasMoreData === false && data.length > 0 && (
          <p className="w-full text-center py-2 text-neut-sec">
            {t('current_products.no_more_products')}
          </p>
        )}
      </div>
    </>
  );
}

function getTableRows(
  productsInventory: ReturnedSellerProductInventory[] | undefined,
  handleActionsClicked: (inventoryId: string, action: string) => void,
  t: TFunction
): TableRow[] {
  if (productsInventory == null) {
    return [];
  }
  return productsInventory.map(
    (inventory: ReturnedSellerProductInventory, idx: number) => ({
      visibility: {
        value: getVisibilityIndicator(
          inventory.sellerProductConfiguration?.status,
          t
        ),
      },
      id: { value: inventory.id },
      productCode: {
        value: inventory.platformProductsLength?.platformProduct?.code || '',
      },
      productName: {
        value: inventory.platformProductsLength?.platformProduct?.name || '',
      },
      length: {
        value: inventory.platformProductsLength
          ? formatLengthWithBasis(
              inventory.platformProductsLength.lengthDisplayValue,
              inventory.platformProductsLength.isNominal
            )
          : '',
      },
      location: {
        value: formatLocationName(inventory.location),
      },
      stock: {
        value:
          inventory.quantity.value.toLocaleString() +
          ' ' +
          inventory.quantity.unit,
        editValue: inventory.quantity.value,
      },
      status: { value: getSellerProductStatus(inventory, t) },
      replenishmentRatePerWeek: {
        value: inventory.sellerProductConfiguration
          ? inventory.sellerProductConfiguration.replenishmentRatePerWeek.value.toLocaleString() +
            ' ' +
            inventory.sellerProductConfiguration.replenishmentRatePerWeek.unit +
            '/week'
          : t('current_products.status.na'),
        editValue:
          inventory.sellerProductConfiguration?.replenishmentRatePerWeek
            .value ?? '',
      },
      readyToShipDays: {
        value:
          inventory.sellerProductConfiguration?.readyToShipDays ??
          t('current_products.status.na'),
        editValue: inventory.sellerProductConfiguration?.readyToShipDays ?? '',
      },
      actions: {
        value: (
          <div className="flex gap-4 items-center justify-center">
            <button
              type="button"
              data-testid={`settings-button-${idx}`}
              className="cursor-pointer"
              onClick={() => handleActionsClicked(inventory.id, 'settings')}
            >
              <Settings size={18} />
            </button>
            <button
              type="button"
              data-testid={`edit-button-${idx}`}
              className="cursor-pointer"
              onClick={() => handleActionsClicked(inventory.id, 'edit')}
            >
              <SquarePen size={18} />
            </button>
          </div>
        ),
      },
    })
  );
}

function getSellerProductStatus(
  inventory: ReturnedSellerProductInventory,
  t: TFunction
): ReactNode {
  const sellerProductConfiguration = inventory.sellerProductConfiguration;
  // Stock health is derived from the configured low/critical thresholds; an
  // unconfigured product has no thresholds, so there is no status to show.
  if (!sellerProductConfiguration) {
    return (
      <span className="text-sm font-medium text-neut-sec">
        {t('current_products.status.na')}
      </span>
    );
  }

  if (
    inventory.quantity.value > sellerProductConfiguration.lowStockLevel.value
  ) {
    return (
      <span className="text-sm font-medium text-success-700 bg-success-100 px-2 py-1 rounded-lg">
        {t('current_products.status.healthy')}
      </span>
    );
  }

  if (
    inventory.quantity.value >
    sellerProductConfiguration.criticalStockLevel.value
  ) {
    return (
      <span className="text-sm font-medium text-warning-600 bg-warning-100 px-2 py-1 rounded-lg">
        {t('current_products.status.low')}
      </span>
    );
  }

  return (
    <span className="text-sm font-medium text-error-500 bg-error-100 px-2 py-1 rounded-lg">
      {t('current_products.status.critical')}
    </span>
  );
}

function getTableColumns(t: TFunction): TableColumn[] {
  return [
    {
      key: 'visibility',
      header: t('current_products.table.headers.visibility'),
      width: 4,
    },
    {
      key: 'productCode',
      header: t('current_products.table.headers.product_code'),
      width: 4,
    },
    {
      key: 'productName',
      header: t('current_products.table.headers.product'),
      width: 7,
    },
    {
      key: 'length',
      header: t('current_products.table.headers.length'),
      width: 4,
    },
    {
      key: 'location',
      header: t('current_products.table.headers.location'),
      width: 9,
    },
    {
      key: 'stock',
      header: t('current_products.table.headers.stock'),
      width: 6,
      isEditable: true,
      editableType: 'number',
    },
    {
      key: 'status',
      header: t('current_products.table.headers.status'),
      width: 5,
    },
    {
      key: 'replenishmentRatePerWeek',
      header: t('current_products.table.headers.replenishment_rate'),
      width: 8,
      isEditable: true,
      editableType: 'number',
    },
    {
      key: 'readyToShipDays',
      header: t('current_products.table.headers.ready_to_ship'),
      width: 6,
      isEditable: true,
      editableType: 'number',
    },
    {
      key: 'actions',
      header: t('current_products.table.headers.actions'),
      stickTo: 'right',
    },
  ];
}

function getVisibilityIndicator(
  status: SellerProductStatus | null | undefined,
  t: TFunction
): ReactNode {
  if (!status) return t('current_products.status.na');
  const STATUS_COLOURS: Record<SellerProductStatus, string> = {
    VISIBLE_TO_BUYERS: 'bg-success-500',
    VISIBLE_TO_MY_BUYERS: 'bg-status-info-600',
    INACTIVE: 'bg-error-500',
  };
  return (
    <div className="w-full flex justify-center">
      <span
        className={`inline-block w-2.5 h-2.5 rounded-full ${STATUS_COLOURS[status]}`}
        title={t(`edit_product.options.status.${status.toLowerCase()}`)}
        aria-label={t(`edit_product.options.status.${status.toLowerCase()}`)}
        aria-hidden="false"
      />
    </div>
  );
}
